import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom-v6'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
} from '@clouway/ui'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { useConfigTemplates, useDeleteConfigTemplate } from '../hooks'

export function ConfigTemplatesList() {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { data: templates, isLoading } = useConfigTemplates()
  const { mutate: remove } = useDeleteConfigTemplate()

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
      <div className="flex justify-end">
        <Button asChild>
          <Link to="/config-templates/new">
            <Plus className="size-4" />
            {tCommon('configTemplates.register')}
          </Link>
        </Button>
      </div>

      {templates && templates.length === 0 && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('configTemplates.list.emptyMessage')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}

      {templates?.map((template) => (
        <Card key={template.id}>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm">{template.name}</CardTitle>
                <p className="text-muted-foreground text-sm">
                  {template.device.modelName} ·{' '}
                  {t(`trackingObjects.types.${template.device.type}` as 'trackingObjects.types.iot')}
                </p>
              </div>
              <div className="flex gap-1">
                <Button asChild size="icon" variant="ghost" aria-label="edit">
                  <Link to={`/config-templates/${template.id}/edit`}>
                    <Pencil className="size-4" />
                  </Link>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" aria-label="delete">
                      <Trash2 className="size-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('configTemplates.list.deleteTemplate')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('configTemplates.list.deleteTemplateDescription', { name: template.name })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(template.id)}>
                        {tCommon('ui.button.confirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardHeader>
          <CardContent />
        </Card>
      ))}
    </div>
  )
}
